import { Header } from '@tanstack/react-table';
import classNames from 'classnames';
import { DataTableColumnMeta } from './types';
import { getFilterVariant, getPinningStyles } from './utils';
import { HeaderDateFilter } from './header-date-filter';
import { HeaderMultiSelectFilter } from './header-multi-select-filter';
import { HeaderRangeFilter } from './header-range-filter';
import { HeaderSelectFilter } from './header-select-filter';
import { HeaderTextFilter } from './header-text-filter';
import styles from './filter-row.module.scss';

// ─────────────────────────────────────
// Filter Cell
// ─────────────────────────────────────

interface FilterCellProps<TData, TValue> {
  header: Header<TData, TValue>;
  effectiveWidth?: number;
}

/**
 * Individual filter cell that renders the appropriate filter input
 * based on column type (text, range, select, multiselect, or date).
 * @param {FilterCellProps} props - Component props
 * @param {Header} props.header - TanStack Table header instance
 * @return {JSX.Element} The filter cell element
 */
function FilterCell<TData, TValue>({
  header,
  effectiveWidth,
}: FilterCellProps<TData, TValue>) {
  const column = header.column;
  const meta = column.columnDef.meta as DataTableColumnMeta | undefined;
  const variant = getFilterVariant(meta);

  // Use effectiveWidth if provided, otherwise fallback
  const cellWidth = effectiveWidth ?? column.getSize();
  const pinStyles = getPinningStyles(column);
  const isPinned = column.getIsPinned();

  const cellStyle = {
    width: cellWidth,
    position: pinStyles.position as React.CSSProperties['position'],
    left: pinStyles.left,
    right: pinStyles.right,
    zIndex: pinStyles.zIndex,
    boxShadow: pinStyles.boxShadow,
  };

  // Skip if column can't be filtered or variant is 'none'
  if (!column.getCanFilter() || variant === 'none') {
    return (
      <td
        className={classNames(styles.filterCell, {
          [styles.filterCellPinned]: isPinned,
        })}
        style={cellStyle}
      />
    );
  }

  return (
    <td
      className={classNames(styles.filterCell, {
        [styles.filterCellPinned]: isPinned,
      })}
      style={cellStyle}
    >
      {variant === 'text' && <HeaderTextFilter column={column} />}
      {variant === 'range' && <HeaderRangeFilter column={column} />}
      {variant === 'select' && <HeaderSelectFilter column={column} />}
      {variant === 'multiselect' && <HeaderMultiSelectFilter column={column} />}
      {variant === 'date' && <HeaderDateFilter column={column} />}
    </td>
  );
}

// ─────────────────────────────────────
// Filter Row
// ─────────────────────────────────────

interface FilterRowProps<TData, TValue> {
  headers: Header<TData, TValue>[];
  effectiveWidths?: Record<string, number>;
}

/**
 * Renders a filter row with inputs for each column
 * @param {FilterRowProps} props - Component props
 * @return {JSX.Element} The filter row element
 */
export function FilterRow<TData, TValue>({
  headers,
  effectiveWidths,
}: FilterRowProps<TData, TValue>) {
  return (
    <tr className={styles.filterRow}>
      {headers.map((header) => (
        <FilterCell
          key={header.id}
          header={header}
          effectiveWidth={effectiveWidths?.[header.column.id]}
        />
      ))}
    </tr>
  );
}
